import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  PlusOutlined,
  QuestionCircleOutlined
} from "@ant-design/icons"
import {
  Button,
  Checkbox,
  Empty,
  Flex,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Tag,
  Tooltip,
  Typography
} from "antd"
import dayjs from "dayjs"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { type MilestoneDependencyWriteData } from "../../../../../api/project-milestone-dependencies.services.ts"
import {
  getProjectMilestone,
  getProjectMilestones
} from "../../../../../api/project-milestones.services.ts"
import {
  type MilestoneDependency,
  MilestoneDependencyType
} from "../../../../../types/project-milestone-dependency.types.ts"
import { type ProjectMilestone } from "../../../../../types/project-phases.types.tsx"
import { TextInput } from "../../../../../../../components/Input/TextInput.tsx"
import { useDebouncedCallback } from "../../../../../../../hooks/use-debounced-callback.ts"
import { MILESTONE_STATUSES } from "../../../../constants/milestone-statuses.ts"
import {
  lagPhrase,
  minLagDaysFor
} from "../../../../constants/milestone-dependency-relationship.ts"
import {
  MilestoneDependencyDatePreview,
  previewsDependencyDates
} from "./MilestoneDependencyDatePreview.tsx"

const { Text, Title } = Typography

const TYPE_INFO: Record<
  MilestoneDependencyType,
  {
    code: string
    label: string
    hint: string
    explain: (source: string, target: string) => string
  }
> = {
  [MilestoneDependencyType.FINISH_TO_START]: {
    code: "FS",
    label: "Finish → Start",
    hint: "Source finishes, target starts",
    explain: (source, target) =>
      `${target} can start after ${source} is finished.`
  },
  [MilestoneDependencyType.START_TO_START]: {
    code: "SS",
    label: "Start → Start",
    hint: "Source starts, target starts",
    explain: (source, target) =>
      `${target} can start after ${source} has started.`
  },
  [MilestoneDependencyType.FINISH_TO_FINISH]: {
    code: "FF",
    label: "Finish → Finish",
    hint: "Source finishes, target finishes",
    explain: (source, target) =>
      `${target} can finish after ${source} is finished.`
  },
  [MilestoneDependencyType.START_TO_FINISH]: {
    code: "SF",
    label: "Start → Finish",
    hint: "Source starts, target finishes",
    explain: (source, target) =>
      `${target} can finish after ${source} has started.`
  }
}

const TYPE_OPTIONS = Object.entries(TYPE_INFO).map(([value, info]) => ({
  value: Number(value) as MilestoneDependencyType,
  label: (
    <Flex vertical style={{ padding: "2px 0" }}>
      <Text>
        {info.label} ({info.code})
      </Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {info.hint}
      </Text>
    </Flex>
  )
}))

// A dependency is one of the things Pending was defined to mean, but the
// status is a person's to choose, so it is offered rather than derived: ticked
// ahead of time from Planned, and left to the user from any other status,
// which was chosen for a reason a new edge does not overrule.
const MarkPendingOffer = ({
  status,
  checked,
  onChange,
  waitingForName
}: {
  status?: number | null
  checked: boolean
  onChange: (checked: boolean) => void
  waitingForName?: string
}) => {
  const { t } = useTranslation()

  if (status === MILESTONE_STATUSES.PENDING) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t("milestoneDependencies.markPending.already")}
      </Text>
    )
  }

  return (
    <Flex vertical gap={2}>
      <Checkbox
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      >
        {t("milestoneDependencies.markPending.label")}
      </Checkbox>
      {waitingForName && (
        <Text type="secondary" style={{ fontSize: 12, paddingLeft: 24 }}>
          {t("milestoneDependencies.markPending.waitingFor", {
            name: waitingForName
          })}
        </Text>
      )}
    </Flex>
  )
}

const OPTIONS_PAGE_SIZE = 50

// The table hands this window one page of milestones, but an edge can reach
// any milestone on the project, so the rest are read from the API as needed.
// Results only accumulate, which makes an out-of-order response harmless.
const useProjectMilestoneLookup = (projectId: string | undefined) => {
  const [fetched, setFetched] = useState<ProjectMilestone[]>([])
  const [searching, setSearching] = useState(false)
  const latestSearch = useRef(0)

  const merge = useCallback((rows: ProjectMilestone[]) => {
    setFetched((current) => {
      const byId = new Map(current.map((m) => [m.id, m]))
      rows.forEach((m) => byId.set(m.id, m))
      return [...byId.values()]
    })
  }, [])

  const search = useCallback(
    async (term: string) => {
      if (!projectId) return
      const seq = ++latestSearch.current
      setSearching(true)
      try {
        const response = await getProjectMilestones({
          lead: projectId,
          page: 1,
          page_size: OPTIONS_PAGE_SIZE,
          ...(term ? { q: term } : {})
        })
        if (response.ok && response.data?.results) merge(response.data.results)
      } finally {
        if (seq === latestSearch.current) setSearching(false)
      }
    },
    [projectId, merge]
  )

  const ensure = useCallback(
    async (id: string) => {
      const response = await getProjectMilestone(id)
      if (response.ok && response.data?.id) merge([response.data])
    },
    [merge]
  )

  return { fetched, searching, search, ensure }
}

const isEdgeBetween = (
  dependency: MilestoneDependency,
  one: string,
  other: string
) =>
  (dependency.source_milestone === one &&
    dependency.target_milestone === other) ||
  (dependency.source_milestone === other && dependency.target_milestone === one)

const RelationshipLegend = () => (
  <Flex vertical gap={10} style={{ maxWidth: 280 }}>
    <Text strong style={{ color: "#fff" }}>
      Dependency types
    </Text>
    {Object.values(TYPE_INFO).map((info) => (
      <Flex vertical key={info.code}>
        <Text style={{ color: "#fff" }}>
          {info.label} ({info.code})
        </Text>
        <Text style={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>
          {info.hint === "Source finishes, target starts" &&
            "The target starts after the source finishes. Example: Demolition finishes → Foundation starts."}
          {info.hint === "Source starts, target starts" &&
            "The target starts once the source has started."}
          {info.hint === "Source finishes, target finishes" &&
            "The target finishes after the source finishes."}
          {info.hint === "Source starts, target finishes" &&
            "The target finishes after the source has started."}
        </Text>
      </Flex>
    ))}
  </Flex>
)

interface MilestoneDependenciesModalProps {
  milestone: ProjectMilestone
  milestones: ProjectMilestone[]
  dependencies: MilestoneDependency[]
  saving: boolean
  onClose: () => void
  onCreate: (data: MilestoneDependencyWriteData) => Promise<boolean>
  onUpdate: (
    id: string,
    data: Partial<MilestoneDependencyWriteData>
  ) => Promise<boolean>
  onDelete: (id: string) => Promise<boolean>
  onMarkPending?: () => Promise<unknown>
}

export function MilestoneDependenciesModal({
  milestone,
  milestones,
  dependencies,
  saving,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
  onMarkPending
}: MilestoneDependenciesModalProps) {
  const { t } = useTranslation()
  const lookup = useProjectMilestoneLookup(milestone.lead)

  const known = useMemo(() => {
    const byId = new Map(lookup.fetched.map((m) => [m.id, m]))
    milestones.forEach((m) => byId.set(m.id, m))
    return [...byId.values()]
  }, [milestones, lookup.fetched])

  const findMilestone = (id: string | undefined) =>
    known.find((m) => m.id === id)

  const nameOf = (id: string, serverName?: string | null): string =>
    serverName ||
    findMilestone(id)?.name ||
    t("milestoneDependencies.fallbackMilestone")

  // Inclusive, like `duration_days`; null when the source has no span an
  // overlap could be measured against.
  const durationOf = (id: string | undefined): number | null => {
    const source = findMilestone(id)
    if (!source) return null
    if (source.start_date && source.end_date) {
      return dayjs(source.end_date).diff(dayjs(source.start_date), "day")
    }
    return source.duration_days && source.duration_days > 0
      ? source.duration_days - 1
      : null
  }

  const related = useMemo(
    () =>
      dependencies.filter(
        (d) =>
          d.source_milestone === milestone.id ||
          d.target_milestone === milestone.id
      ),
    [dependencies, milestone.id]
  )

  // A milestone already joined to this one in either direction is refused by the
  // server — same direction as a duplicate edge, opposite as a circular
  // dependency — so offering it would only surface a raw API error.
  const otherMilestones = useMemo(
    () =>
      known.filter(
        (m) =>
          m.id !== milestone.id &&
          !related.some((d) => isEdgeBetween(d, m.id, milestone.id))
      ),
    [known, milestone.id, related]
  )

  const [showAddForm, setShowAddForm] = useState(false)

  const { search: searchMilestones, ensure: ensureMilestone } = lookup
  useEffect(() => {
    void searchMilestones("")
  }, [searchMilestones])

  const debouncedSearch = useDebouncedCallback(
    (term: string) => void searchMilestones(term.trim()),
    400
  )

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editType, setEditType] = useState<MilestoneDependencyType>(
    MilestoneDependencyType.FINISH_TO_START
  )
  const [editLag, setEditLag] = useState<number>(0)

  const isPlanned = milestone.status === MILESTONE_STATUSES.PLANNED

  const [dependsOn, setDependsOn] = useState<string | undefined>(undefined)
  const [addType, setAddType] = useState<MilestoneDependencyType>(
    MilestoneDependencyType.FINISH_TO_START
  )
  const [addLag, setAddLag] = useState<number>(0)
  const [markPending, setMarkPending] = useState(isPlanned)

  const startEdit = (dependency: MilestoneDependency) => {
    // The lag floor is measured on the source's span.
    if (!findMilestone(dependency.source_milestone)) {
      void ensureMilestone(dependency.source_milestone)
    }
    setEditingId(dependency.id)
    setEditType(dependency.type)
    setEditLag(dependency.lag_days)
  }

  const handleSaveEdit = async () => {
    if (!editingId) return
    const ok = await onUpdate(editingId, { type: editType, lag_days: editLag })
    if (ok) setEditingId(null)
  }

  const openAddForm = () => {
    setDependsOn(undefined)
    setAddType(MilestoneDependencyType.FINISH_TO_START)
    setAddLag(0)
    setMarkPending(isPlanned)
    setShowAddForm(true)
  }

  const handleAdd = async () => {
    if (!dependsOn) return
    const ok = await onCreate({
      source_milestone: dependsOn,
      target_milestone: milestone.id,
      type: addType,
      lag_days: addLag
    })
    if (!ok) return
    // Only after the edge exists: creating it can still be called off at the
    // schedule-impact confirmation, and a cancelled dependency must leave the
    // status where it was.
    if (markPending && onMarkPending) await onMarkPending()
    setShowAddForm(false)
  }

  const addExplanation = dependsOn
    ? TYPE_INFO[addType].explain(nameOf(dependsOn), milestone.name) +
      (addLag ? ` (${lagPhrase(t, addLag)})` : "")
    : null

  return (
    <Modal
      open
      title={
        <Flex vertical gap={0}>
          <Title level={5} style={{ margin: 0 }}>
            Dependencies
          </Title>
          <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
            {milestone.name}
          </Text>
        </Flex>
      }
      onCancel={onClose}
      footer={null}
      width={560}
    >
      <Flex vertical gap={10} style={{ marginTop: 16, marginBottom: 16 }}>
        {related.length === 0 && !showAddForm && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No dependencies yet"
            style={{ margin: "8px 0 16px" }}
          />
        )}

        {related.map((dependency) => {
          const isEditing = editingId === dependency.id
          const isSource = dependency.source_milestone === milestone.id
          // Phrase from this milestone's point of view: either it depends on
          // the other one, or the other one depends on it.
          const otherName = isSource
            ? nameOf(
                dependency.target_milestone,
                dependency.target_milestone_name
              )
            : nameOf(
                dependency.source_milestone,
                dependency.source_milestone_name
              )

          return (
            <Flex
              key={dependency.id}
              vertical
              gap={6}
              style={{
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                padding: "10px 12px"
              }}
            >
              <Flex align="center" justify="space-between" gap="small">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {isSource ? "Required for" : "Depends on"}
                </Text>
                {!isEditing && (
                  <Flex gap={4}>
                    <Button
                      size="small"
                      type="text"
                      icon={<EditOutlined />}
                      aria-label="Edit dependency"
                      onClick={() => startEdit(dependency)}
                    />
                    <Popconfirm
                      title="Remove dependency?"
                      description={
                        <span>
                          &quot;{isSource ? otherName : milestone.name}&quot;
                          depends on
                          <br />
                          &quot;{isSource ? milestone.name : otherName}&quot;.
                        </span>
                      }
                      okText="Remove dependency"
                      cancelText="Cancel"
                      okButtonProps={{ danger: true, loading: saving }}
                      onConfirm={() => onDelete(dependency.id)}
                    >
                      <Button
                        size="small"
                        disabled={saving}
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label="Delete dependency"
                      />
                    </Popconfirm>
                  </Flex>
                )}
              </Flex>

              <Flex align="center" gap={6}>
                <LinkOutlined style={{ color: "var(--ant-color-primary)" }} />
                <Text strong ellipsis>
                  {otherName}
                </Text>
              </Flex>

              {isEditing ? (
                <Flex gap="small" align="center" wrap>
                  <Select
                    size="small"
                    style={{ width: 190 }}
                    options={TYPE_OPTIONS.map((o) => ({
                      ...o,
                      label:
                        TYPE_INFO[o.value].label +
                        " (" +
                        TYPE_INFO[o.value].code +
                        ")"
                    }))}
                    value={editType}
                    onChange={setEditType}
                  />
                  <InputNumber
                    size="small"
                    addonAfter="days"
                    style={{ width: 110 }}
                    min={minLagDaysFor(
                      editType,
                      durationOf(dependency.source_milestone)
                    )}
                    value={editLag}
                    onChange={(value) => setEditLag(value ?? 0)}
                  />
                  <Button
                    size="small"
                    type="primary"
                    loading={saving}
                    onClick={handleSaveEdit}
                  >
                    Save
                  </Button>
                  <Button size="small" onClick={() => setEditingId(null)}>
                    Cancel
                  </Button>
                </Flex>
              ) : (
                <Flex align="center" gap={6}>
                  <Tag style={{ marginInlineEnd: 0 }}>
                    {TYPE_INFO[dependency.type].label}
                  </Tag>
                  {dependency.lag_days !== 0 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {lagPhrase(t, dependency.lag_days)}
                    </Text>
                  )}
                </Flex>
              )}
            </Flex>
          )
        })}
      </Flex>

      {showAddForm ? (
        <Flex
          vertical
          gap={12}
          style={{
            borderTop: related.length ? "1px solid #f0f0f0" : "none",
            paddingTop: related.length ? 16 : 0
          }}
        >
          <Flex vertical gap={0}>
            <Text strong>Add dependency</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Set a relationship between two milestones.
            </Text>
          </Flex>

          <Flex vertical gap={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Milestone
            </Text>
            <TextInput value={milestone.name} disabled />
          </Flex>

          <Flex vertical gap={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Depends on
            </Text>
            <Select
              showSearch
              allowClear
              placeholder="Select milestone"
              optionFilterProp="label"
              value={dependsOn}
              onChange={setDependsOn}
              onSearch={debouncedSearch}
              loading={lookup.searching}
              options={otherMilestones.map((m) => ({
                value: m.id,
                label: m.name || t("milestoneDependencies.fallbackMilestone")
              }))}
            />
          </Flex>

          <Flex gap="small">
            <Flex vertical gap={4} style={{ flex: 1 }}>
              <Flex align="center" gap={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Relationship
                </Text>
                <Tooltip title={<RelationshipLegend />} color="#1f2937">
                  <QuestionCircleOutlined
                    style={{ fontSize: 12, color: "#8c8c8c" }}
                  />
                </Tooltip>
              </Flex>
              <Select
                options={TYPE_OPTIONS}
                value={addType}
                onChange={setAddType}
                popupMatchSelectWidth={320}
              />
            </Flex>
            <Flex vertical gap={4}>
              <Flex align="center" gap={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Lag (optional)
                </Text>
                <Tooltip title={t("milestoneDependencies.lagHint")}>
                  <QuestionCircleOutlined
                    style={{ fontSize: 12, color: "#8c8c8c" }}
                  />
                </Tooltip>
              </Flex>
              <InputNumber
                addonAfter="days"
                min={minLagDaysFor(addType, durationOf(dependsOn))}
                value={addLag}
                onChange={(value) => setAddLag(value ?? 0)}
              />
            </Flex>
          </Flex>

          <MarkPendingOffer
            status={milestone.status}
            checked={markPending}
            onChange={setMarkPending}
            waitingForName={dependsOn ? nameOf(dependsOn) : undefined}
          />

          {addExplanation && (
            <Flex
              align="flex-start"
              gap={8}
              style={{
                background: "#f0f7f6",
                border: "1px solid #d5ebe8",
                borderRadius: 8,
                padding: "8px 10px"
              }}
            >
              <InfoCircleOutlined
                style={{ color: "var(--ant-color-primary)", marginTop: 2 }}
              />
              <Text style={{ fontSize: 12.5 }}>{addExplanation}</Text>
            </Flex>
          )}

          {dependsOn && previewsDependencyDates(milestone) && (
            <MilestoneDependencyDatePreview
              milestone={milestone}
              milestones={known}
              dependencies={dependencies}
              sourceId={dependsOn}
              type={addType}
              lagDays={addLag}
              nameOf={(id) => nameOf(id)}
            />
          )}

          <Flex justify="flex-end" gap={8}>
            <Button onClick={() => setShowAddForm(false)}>Cancel</Button>
            <Button
              type="primary"
              disabled={!dependsOn}
              loading={saving}
              onClick={handleAdd}
            >
              Add dependency
            </Button>
          </Flex>
        </Flex>
      ) : (
        <Button
          type="dashed"
          block
          icon={<PlusOutlined />}
          disabled={!otherMilestones.length}
          onClick={openAddForm}
          style={{ marginTop: related.length ? 4 : 0 }}
        >
          Add dependency
        </Button>
      )}
    </Modal>
  )
}
